import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Users, Shield, Baby, Home, BookOpen, ArrowLeft, Clock, MessageCircle } from 'lucide-react';

/**
 * Family & Continuity Education Page
 * 
 * Learning modules for parents navigating dating, blended families,
 * and homeschooling within non-traditional relationship structures.
 */
const FamilyContinuityEducation = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/90 border-b border-slate-800 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-slate-400 hover:text-pink-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <div className="flex items-center space-x-2">
              <Heart className="w-6 h-6 text-pink-500 fill-current" />
              <span className="font-semibold text-xl text-white">Unity Love</span>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-slate-400 hover:text-pink-400 transition-colors"
            >
              <Home className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="rounded-3xl bg-slate-900/60 border border-slate-800 p-8 lg:p-12 mb-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Baby className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Family & Continuity
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Calm, practical guidance for parents navigating dating, blended families,
            and raising children in non-traditional structures.
          </p>
          <p className="text-sm text-slate-500 mt-4">
            No judgment. No preaching. Just what works.
          </p>
        </div>

        {/* Module 1: Parenting While Dating */}
        <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-pink-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              Module 1: Parenting While Dating
            </h2>
          </div>
          <p className="text-slate-400 mb-4 text-sm">Boundaries first, romance second</p>
          
          <div className="space-y-4">
            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">Core Principle</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Your children's stability comes before your dating life's excitement. 
                This isn't about guilt or sacrifice — it's about sequencing. 
                Get your boundaries clear before you invite anyone new into your world.
              </p>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">Practical Guidelines</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">•</span>
                  <span>Date on your own time first — not during custody time, not when kids need attention</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">•</span>
                  <span>Don't introduce new partners until you've established genuine stability (3-6 months minimum)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">•</span>
                  <span>Keep dating life and parenting life in separate mental containers at first</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">•</span>
                  <span>Be honest with partners: "I have kids, they're my priority, and here's what that looks like"</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <p className="text-amber-200 text-sm">
                <strong>Remember:</strong> Someone who doesn't respect your boundaries around your kids 
                isn't going to respect your boundaries anywhere else.
              </p>
            </div>
          </div>
        </div>

        {/* Module 2: Introducing Partners to Children */}
        <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-pink-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              Module 2: Introducing Partners to Children
            </h2>
          </div>
          <p className="text-slate-400 mb-4 text-sm">Timing, language, and the long game</p>
          
          <div className="space-y-4">
            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">When to Introduce</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span>After the relationship has proven stable over time (not just intense)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span>When you can confidently say "this person will be around"</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span>When your partner genuinely understands what they're stepping into</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400">✗</span>
                  <span>Not during the honeymoon phase when everything feels perfect</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">Language That Works</h3>
              <div className="space-y-3 text-slate-300 text-sm">
                <p>Start simple: <em>"This is my friend [Name]"</em></p>
                <p>As things progress: <em>"[Name] is someone I care about a lot. They're going to be around more."</em></p>
                <p>Answer questions honestly but age-appropriately. Don't overshare, don't lie.</p>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">The Long Game</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Kids don't need to love your partner immediately. They need to feel safe.
                Let relationships develop naturally. Don't force bonding, shared activities, or "family time."
                Consistency and calm presence matter more than big gestures.
              </p>
            </div>
          </div>
        </div>

        {/* Module 3: Blended & Multi-Adult Households */}
        <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
              <Home className="w-5 h-5 text-pink-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              Module 3: Blended & Multi-Adult Households
            </h2>
          </div>
          <p className="text-slate-400 mb-4 text-sm">Roles, clarity, and stability</p>
          
          <div className="space-y-4">
            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">Core Challenges</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">•</span>
                  <span>Who disciplines? Who doesn't?</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">•</span>
                  <span>How do we present a united front without confusing the kids?</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">•</span>
                  <span>What if the kids don't accept a new adult?</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">What Works</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span><strong>Clear roles:</strong> Bio-parents lead on discipline early on. Partners support, not override.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span><strong>Consistent routines:</strong> Same bedtimes, same rules, same expectations across adults.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span><strong>Private adult conversations:</strong> Disagreements happen behind closed doors, not in front of kids.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span><strong>Time:</strong> It takes 2-5 years for blended families to feel "normal." Don't rush.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Module 4: Co-Parenting Without Emotional Bleed */}
        <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-pink-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              Module 4: Co-Parenting Without Emotional Bleed
            </h2>
          </div>
          <p className="text-slate-400 mb-4 text-sm">Communication protocols for separated or multi-parent situations</p>
          
          <div className="space-y-4">
            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">The Problem</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                When co-parents haven't fully processed the end of their romantic relationship,
                "kid logistics" conversations become battlegrounds for unresolved hurt.
                Kids notice. Kids suffer.
              </p>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">Communication Protocols</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">1.</span>
                  <span><strong>Business-only mode:</strong> Text about logistics. Save emotional processing for therapy or friends.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">2.</span>
                  <span><strong>48-hour rule:</strong> If a message makes you angry, wait 48 hours before responding.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">3.</span>
                  <span><strong>BIFF method:</strong> Keep messages Brief, Informative, Friendly, and Firm.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">4.</span>
                  <span><strong>Parallel parenting:</strong> If co-parenting is too triggering, parallel parent (minimal contact, each house has its own rules).</span>
                </li>
              </ul>
            </div>

            <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4">
              <p className="text-rose-200 text-sm">
                <strong>Hard truth:</strong> Your kids don't need you to be friends with your co-parent.
                They need you to not use them as messengers, weapons, or therapists.
              </p>
            </div>
          </div>
        </div>

        {/* Module 5: Children as Observers */}
        <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-pink-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              Module 5: Children as Observers, Not Participants
            </h2>
          </div>
          <p className="text-slate-400 mb-4 text-sm">Privacy, safety, and age-appropriate boundaries</p>
          
          <div className="space-y-4">
            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">The Line</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Children can know you have relationships. They can know you love people.
                They don't need to know the details of your romantic or intimate life.
                They don't need to witness adult conflicts, breakups, or relationship drama.
              </p>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">What Kids Need to Know</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-green-400 text-sm font-semibold mb-2">Age-appropriate:</p>
                  <ul className="space-y-1 text-slate-300 text-sm">
                    <li>• "Mom/Dad has friends who care about us"</li>
                    <li>• "Our family looks different, and that's okay"</li>
                    <li>• "You can always ask questions"</li>
                  </ul>
                </div>
                <div>
                  <p className="text-red-400 text-sm font-semibold mb-2">Not their business:</p>
                  <ul className="space-y-1 text-slate-300 text-sm">
                    <li>• Relationship labels and structures</li>
                    <li>• Adult conflicts or jealousy</li>
                    <li>• Details about intimacy or dating</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <p className="text-amber-200 text-sm">
                <strong>Safety note:</strong> Any adult in your children's lives should be someone you'd trust alone with them.
                Trust is earned over time, not assumed because of a relationship title.
              </p>
            </div>
          </div>
        </div>

        {/* Module 6: Homeschooling Basics */}
        <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-pink-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              Module 6: Homeschooling Basics
            </h2>
          </div>
          <p className="text-slate-400 mb-4 text-sm">Philosophy and daily structure (no legal claims)</p>
          
          <div className="space-y-4">
            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">Why Families Choose Homeschooling</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">•</span>
                  <span>Flexibility for non-traditional schedules (custody arrangements, travel, work)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">•</span>
                  <span>Ability to teach values and worldviews directly</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">•</span>
                  <span>One-on-one attention and pacing based on the child's needs</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400">•</span>
                  <span>Protection from environments that don't align with family values</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">Common Approaches</h3>
              <div className="space-y-3 text-slate-300 text-sm">
                <p><strong>Structured curriculum:</strong> Follows a set program, similar to traditional school.</p>
                <p><strong>Eclectic:</strong> Mix of curriculum, projects, and real-world learning.</p>
                <p><strong>Unschooling:</strong> Child-led, interest-based learning with minimal formal instruction.</p>
                <p><strong>Classical:</strong> Focus on trivium (grammar, logic, rhetoric) and great books.</p>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-3">Daily Structure Tips</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" />
                  <span>Start with a consistent morning routine (even if learning starts later)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" />
                  <span>Academic work in 2-4 hour blocks, not 8-hour school days</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" />
                  <span>Build in movement, play, and outdoor time every day</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" />
                  <span>Socialization through co-ops, sports, classes, and community involvement</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
              <p className="text-slate-400 text-xs">
                <strong>Note:</strong> Homeschooling laws vary by region. This content is educational, not legal guidance.
                Always research your local requirements before beginning.
              </p>
            </div>
          </div>
        </div>

        {/* Closing Note */}
        <div className="rounded-2xl bg-gradient-to-r from-pink-500/10 to-rose-500/10 border border-pink-500/20 p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Family is what you build
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto">
            Whether you're a single parent dating for the first time, part of a poly family 
            with multiple adults, or somewhere in between — what matters is stability, 
            honesty, and putting your children's wellbeing at the center.
          </p>
          <p className="text-slate-400 text-sm mt-4">
            There's no perfect structure. There's only the one you build with care.
          </p>
        </div>

        {/* Navigation */}
        <div className="mt-8 flex justify-center gap-4">
          <button
            onClick={() => navigate('/education/love-your-way')}
            className="px-6 py-3 bg-slate-800 border border-slate-700 text-white rounded-xl hover:bg-slate-700 transition"
          >
            ← Love Your Way Guide
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-pink-600 text-white rounded-xl hover:bg-pink-500 transition"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default FamilyContinuityEducation;
